import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Plus, Trash2, Wand2, ChevronDown, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';

const STAGE_LABELS = {
  lead: 'Lead',
  primeiro_contato: 'Primeiro Contato',
  proposta_enviada: 'Proposta Enviada',
  negociacao: 'Negociação',
  fechado: 'Fechado',
  perdido: 'Perdido',
};

function fillVars(message, lead) {
  return message
    .replace(/\{\{nome\}\}/gi, lead?.contact_name || lead?.title || 'Cliente')
    .replace(/\{\{empresa\}\}/gi, lead?.company_name || 'sua empresa')
    .replace(/\{\{servico\}\}/gi, lead?.title || 'nosso serviço');
}

function openWhatsApp(phone, message) {
  if (!phone) { toast.error('Lead sem telefone cadastrado'); return; }
  const cleaned = phone.replace(/\D/g, '');
  const number = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  const text = encodeURIComponent(message);
  window.open(`https://wa.me/${number}?text=${text}`, '_blank');
}

export default function MessageTemplatesModal({ open, onClose, lead }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filteredStage, setFilteredStage] = useState('todos');
  const [showCreate, setShowCreate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', stage: 'lead', message: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) loadTemplates();
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/leads/templates');
      setTemplates(res.data);
      if (res.data.length > 0) setSelectedTemplate(res.data[0]);
    } catch (e) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newTemplate.name || !newTemplate.message) {
      toast.error('Preencha nome e mensagem');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/leads/templates', newTemplate);
      setTemplates(p => [...p, res.data]);
      setSelectedTemplate(res.data);
      setNewTemplate({ name: '', stage: 'lead', message: '' });
      setShowCreate(false);
      toast.success('Template criado!');
    } catch (e) {
      toast.error('Erro ao criar template');
    } finally {
      setSaving(false);
    }
  };

  const filteredTemplates = filteredStage === 'todos'
    ? templates
    : templates.filter(t => t.stage === filteredStage);

  const previewMessage = selectedTemplate ? fillVars(selectedTemplate.message, lead) : '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto premium-scrollbar">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-violet-600" />
            Templates de Mensagem
          </DialogTitle>
          <DialogDescription>
            Selecione um template, veja o preview e envie via WhatsApp com um clique.
          </DialogDescription>
        </DialogHeader>

        {/* Filtro por etapa */}
        <div className="flex gap-2 flex-wrap">
          {['todos', ...Object.keys(STAGE_LABELS)].map(stage => (
            <button
              key={stage}
              onClick={() => setFilteredStage(stage)}
              className={`text-xs px-3 py-1 rounded-full border transition-all ${
                filteredStage === stage
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'
              }`}
            >
              {stage === 'todos' ? 'Todos' : STAGE_LABELS[stage]}
            </button>
          ))}
        </div>

        {/* Lista de Templates */}
        <div className="grid grid-cols-2 gap-2 mt-1">
          {loading ? (
            <p className="col-span-2 text-center text-sm text-slate-400 py-4">Carregando...</p>
          ) : filteredTemplates.length === 0 ? (
            <p className="col-span-2 text-center text-sm text-slate-400 py-4">Nenhum template encontrado</p>
          ) : filteredTemplates.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedTemplate(t)}
              className={`text-left p-3 rounded-xl border transition-all ${
                selectedTemplate?.id === t.id
                  ? 'border-violet-400 bg-violet-50'
                  : 'border-slate-100 bg-white hover:border-violet-200'
              }`}
            >
              <p className="text-sm font-semibold text-slate-900">{t.name}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{t.message}</p>
              <span className="text-[10px] text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                {STAGE_LABELS[t.stage] || t.stage}
              </span>
            </button>
          ))}
        </div>

        {/* Preview da mensagem */}
        {selectedTemplate && (
          <div className="mt-2 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-500 font-medium mb-2 flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" /> Preview (com dados do lead)
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{previewMessage}</p>
            
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white gap-1.5 flex-1"
                onClick={() => openWhatsApp(lead?.contact_phone, previewMessage)}
              >
                <MessageCircle className="h-4 w-4" />
                Enviar via WhatsApp
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(previewMessage);
                  toast.success('Mensagem copiada!');
                }}
              >
                Copiar
              </Button>
            </div>
          </div>
        )}

        {/* Criar novo template */}
        {showCreate ? (
          <div className="mt-2 p-4 bg-white border border-slate-200 rounded-xl space-y-3">
            <p className="text-sm font-semibold text-slate-800">Novo Template</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome do template</Label>
                <Input
                  value={newTemplate.name}
                  onChange={e => setNewTemplate(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: Follow-up Rápido"
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Etapa do funil</Label>
                <Select value={newTemplate.stage} onValueChange={v => setNewTemplate(p => ({ ...p, stage: v }))}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STAGE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">
                Mensagem <span className="text-slate-400">(use &#123;&#123;nome&#125;&#125;, &#123;&#123;empresa&#125;&#125;, &#123;&#123;servico&#125;&#125;)</span>
              </Label>
              <textarea
                className="w-full mt-1 min-h-[100px] px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-none"
                value={newTemplate.message}
                onChange={e => setNewTemplate(p => ({ ...p, message: e.target.value }))}
                placeholder="Olá {{nome}}! Tudo bem? Gostaria de apresentar uma proposta para a {{empresa}}..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} className="gap-1.5">
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Salvando...' : 'Salvar Template'}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 mt-1"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="h-4 w-4" /> Criar novo template
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
